import { Box, keyframes, styled } from "@mui/material";
import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

interface EasterEggProps {
  children: React.ReactNode;
  triggerOnDrag?: boolean;
  triggerOnClick?: boolean;
}

// Define keyframe animations
const floatUpAnimation = keyframes`
  0% { transform: translateY(0) scale(0.8); opacity: 0; }
  20% { opacity: 1; transform: translateY(-20px) scale(1); }
  80% { opacity: 0.8; transform: translateY(-80px) scale(1.1); }
  100% { transform: translateY(-120px) scale(0.8); opacity: 0; }
`;

const floatDiagonalAnimation = keyframes`
  0% { transform: translate(0, 0) rotate(0deg) scale(0.8); opacity: 0; }
  20% { opacity: 1; transform: translate(20px, -20px) rotate(15deg) scale(1); }
  80% { opacity: 0.8; transform: translate(80px, -80px) rotate(30deg) scale(1.1); }
  100% { transform: translate(120px, -120px) rotate(45deg) scale(0.8); opacity: 0; }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(0.6); opacity: 0; filter: blur(2px); }
  25% { transform: scale(1.3); opacity: 1; filter: blur(0px); }
  50% { transform: scale(0.9); opacity: 0.9; filter: blur(0px); }
  75% { transform: scale(1.1); opacity: 0.8; filter: blur(0px); }
  100% { transform: scale(1); opacity: 0; filter: blur(1px); }
`;

const floatZigzagAnimation = keyframes`
  0% { transform: translate(0, 0) rotate(0deg) scale(0.8); opacity: 0; }
  20% { transform: translate(50px, -30px) rotate(10deg) scale(1); opacity: 1; }
  40% { transform: translate(20px, -60px) rotate(-5deg) scale(1.1); opacity: 0.9; }
  60% { transform: translate(70px, -90px) rotate(15deg) scale(1); opacity: 0.8; }
  80% { transform: translate(40px, -120px) rotate(-10deg) scale(1.1); opacity: 0.6; }
  100% { transform: translate(100px, -150px) rotate(0deg) scale(0.8); opacity: 0; }
`;

const stampAnimation = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(3) rotate(-15deg) perspective(500px) rotateX(10deg);
    opacity: 0;
    filter: blur(8px);
  }
  15% {
    transform: translate(-50%, -50%) scale(1.2) rotate(-15deg) perspective(500px) rotateX(5deg);
    opacity: 1;
    filter: blur(0px);
  }
  20% {
    transform: translate(-50%, -50%) scale(1) rotate(-15deg) perspective(500px) rotateX(0deg);
    opacity: 1;
    filter: blur(0px);
  }
  25% {
    transform: translate(-50%, -50%) scale(1.05) rotate(-15deg) perspective(500px) rotateX(0deg);
    opacity: 1;
  }
  30% {
    transform: translate(-50%, -50%) scale(1) rotate(-15deg) perspective(500px) rotateX(0deg);
    opacity: 1;
  }
  85% {
    transform: translate(-50%, -50%) scale(1) rotate(-15deg) perspective(500px) rotateX(0deg);
    opacity: 1;
    filter: blur(0px);
  }
  100% {
    transform: translate(-50%, -50%) scale(0) rotate(-15deg) perspective(500px) rotateX(5deg);
    opacity: 0;
    filter: blur(2px);
  }
`;

// Create an array of modern brand colors (indigo/blue theme)
const modernColors = ["#4f46e5", "#3b82f6", "#6366f1", "#8b5cf6", "#06b6d4"];

// Styled components with modern design
const MoneySign = styled("div")({
  position: "absolute",
  fontSize: "2rem",
  fontWeight: "bold",
  background: "/* @noflip */ linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  zIndex: 1500,
  userSelect: "none",
  pointerEvents: "none",
  animation: `${floatUpAnimation} 1.8s ease-out forwards`,
  filter: "drop-shadow(0 2px 4px rgba(34, 197, 94, 0.3))",
});

const CraveIcon = styled("div")({
  position: "absolute",
  fontSize: "2rem",
  fontWeight: "bold",
  background: "/* @noflip */ linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  zIndex: 1500,
  userSelect: "none",
  pointerEvents: "none",
  animation: `${floatUpAnimation} 1.8s ease-out forwards`,
  filter: "drop-shadow(0 2px 4px rgba(79, 70, 229, 0.3))",
});

const ClassicStar = styled("div")({
  position: "absolute",
  fontSize: "2.2rem",
  fontWeight: "bold",
  background: "/* @noflip */ linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  zIndex: 1500,
  userSelect: "none",
  pointerEvents: "none",
  filter: "drop-shadow(0 2px 8px rgba(245, 158, 11, 0.4))",
});

const ModernStar = styled("div")({
  position: "absolute",
  fontSize: "2.2rem",
  fontWeight: "bold",
  background: "/* @noflip */ linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  zIndex: 1500,
  userSelect: "none",
  pointerEvents: "none",
  filter: "drop-shadow(0 2px 8px rgba(79, 70, 229, 0.4))",
});

const ModernStamp = styled("div")({
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%) rotate(-15deg) perspective(500px) rotateX(0deg)",
  fontSize: "5rem",
  fontWeight: "800",
  background: "/* @noflip */ linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  border: "0.6rem solid transparent",
  borderRadius: "1.5rem",
  padding: "2rem 3.5rem",
  zIndex: 9999,
  userSelect: "none",
  pointerEvents: "none",
  boxShadow: "0 20px 50px rgba(79, 70, 229, 0.3), 0 8px 16px rgba(0, 0, 0, 0.1)",
  animation: `${stampAnimation} 2.5s ease-out forwards`,
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(12px)",
  width: "auto",
  height: "auto",
  margin: "0 auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderImage: "/* @noflip */ linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%) 1",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: "-0.6rem",
    borderRadius: "1.5rem",
    padding: "0.6rem",
    background: "/* @noflip */ linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)",
    mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    maskComposite: "xor",
    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
  },
});

// Function to play a more sophisticated notification sound
const playNotificationSound = () => {
  try {
    if (window.AudioContext || (window as any).webkitAudioContext) {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const context = new AudioContextClass();

      const playNote = (
        frequency: number,
        startTime: number,
        duration: number,
        gain: number = 0.08,
      ) => {
        const oscillator = context.createOscillator();
        const gainNode = context.createGain();
        const filter = context.createBiquadFilter();

        oscillator.connect(filter);
        filter.connect(gainNode);
        gainNode.connect(context.destination);

        // Use a warmer sine wave with slight filtering
        oscillator.type = "sine";
        oscillator.frequency.value = frequency;

        // Add a subtle low-pass filter for warmth
        filter.type = "lowpass";
        filter.frequency.value = frequency * 2;
        filter.Q.value = 1;

        // Smooth envelope
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(gain, startTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };

      // Play a cheerful major chord progression (C - E - G - C)
      const now = context.currentTime;
      playNote(523.25, now, 0.15); // C5
      playNote(659.25, now + 0.1, 0.15); // E5
      playNote(783.99, now + 0.2, 0.15); // G5
      playNote(1046.5, now + 0.3, 0.2); // C6 (longer final note)

      return;
    }

    // Fallback
    if ("Audio" in window) {
      const audio = new Audio(
        "data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU" +
          Array(10).join("A"),
      );
      audio.volume = 0.03;
      audio.play().catch(() => console.log("Browser prevented audio playback"));
    }
  } catch {
    console.log("Browser audio feature not available");
  }
};

export const EasterEgg: React.FC<EasterEggProps> = ({
  children,
  triggerOnDrag = true,
  triggerOnClick = false,
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [showEffect, setShowEffect] = useState(false);
  const [moneySigns, setMoneySigns] = useState<Array<{ id: number; x: number; y: number }>>([]);
  const [craveIcons, setCraveIcons] = useState<Array<{ id: number; x: number; y: number }>>([]);
  const [classicStars, setClassicStars] = useState<
    Array<{ id: number; x: number; y: number; size: number; animation: number }>
  >([]);
  const [modernStars, setModernStars] = useState<
    Array<{ id: number; x: number; y: number; size: number; color: string; animation: number }>
  >([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const particleCounter = useRef(0);

  // Handler for drag start
  const handleDragStart = () => {
    if (triggerOnDrag) {
      setIsDragging(true);
    }
  };

  // Handler for drag end
  const handleDragEnd = () => {
    if (triggerOnDrag && isDragging) {
      triggerEasterEgg();
    }
    setIsDragging(false);
  };

  // Handler for click
  const handleClick = () => {
    if (triggerOnClick) {
      triggerEasterEgg();
    }
  };

  // Function to trigger the easter egg effect
  const triggerEasterEgg = () => {
    // Play notification sound
    playNotificationSound();

    // Show particles at random positions
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Generate money signs
    const newMoneySigns = Array.from({ length: 8 }, () => ({
      id: particleCounter.current++,
      x: Math.random() * windowWidth,
      y: Math.random() * windowHeight,
    }));

    // Generate Crave icons
    const newCraveIcons = Array.from({ length: 8 }, () => ({
      id: particleCounter.current++,
      x: Math.random() * windowWidth,
      y: Math.random() * windowHeight,
    }));

    // Generate classic stars
    const newClassicStars = Array.from({ length: 10 }, () => {
      const size = Math.random() * 0.8 + 1.8; // Sizes between 1.8rem and 2.6rem
      const animationIndex = Math.floor(Math.random() * 3);

      return {
        id: particleCounter.current++,
        x: Math.random() * windowWidth,
        y: Math.random() * windowHeight,
        size,
        animation: animationIndex,
      };
    });

    // Generate modern stars with different properties
    const newModernStars = Array.from({ length: 10 }, () => {
      const size = Math.random() * 0.8 + 1.8; // Sizes between 1.8rem and 2.6rem
      const colorIndex = Math.floor(Math.random() * modernColors.length);
      const animationIndex = Math.floor(Math.random() * 3);

      return {
        id: particleCounter.current++,
        x: Math.random() * windowWidth,
        y: Math.random() * windowHeight,
        size,
        color: modernColors[colorIndex],
        animation: animationIndex,
      };
    });

    setMoneySigns(newMoneySigns);
    setCraveIcons(newCraveIcons);
    setClassicStars(newClassicStars);
    setModernStars(newModernStars);
    setShowEffect(true);

    // Hide the effects after a delay
    setTimeout(() => {
      setShowEffect(false);
      setMoneySigns([]);
      setCraveIcons([]);
      setClassicStars([]);
      setModernStars([]);
    }, 3000);
  };

  // Create a portal for the stamp
  const renderStamp = () => {
    if (!showEffect) return null;

    return ReactDOM.createPortal(
      <ModernStamp>{t("common.easterEgg.text.lgtm")}</ModernStamp>,
      document.body,
    );
  };

  // Create a portal for the particles
  const renderParticles = () => {
    if (
      moneySigns.length === 0 &&
      craveIcons.length === 0 &&
      classicStars.length === 0 &&
      modernStars.length === 0
    )
      return null;

    return ReactDOM.createPortal(
      <>
        {moneySigns.map((sign) => (
          <MoneySign
            key={sign.id}
            sx={{
              left: `${sign.x}px`,
              top: `${sign.y}px`,
            }}
          >
            $
          </MoneySign>
        ))}
        {craveIcons.map((icon) => (
          <CraveIcon
            key={icon.id}
            sx={{
              left: `${icon.x}px`,
              top: `${icon.y}px`,
            }}
          >
            ◇
          </CraveIcon>
        ))}
        {classicStars.map((star) => {
          let animationStyle;
          switch (star.animation) {
            case 0:
              animationStyle = `${floatDiagonalAnimation} 2s ease-out forwards`;
              break;
            case 1:
              animationStyle = `${pulseAnimation} 2.2s ease-in-out forwards`;
              break;
            case 2:
              animationStyle = `${floatZigzagAnimation} 2.5s ease-in-out forwards`;
              break;
            default:
              animationStyle = `${floatDiagonalAnimation} 2s ease-out forwards`;
          }

          return (
            <ClassicStar
              key={star.id}
              sx={{
                left: `${star.x}px`,
                top: `${star.y}px`,
                fontSize: `${star.size}rem`,
                animation: animationStyle,
              }}
            >
              ★
            </ClassicStar>
          );
        })}
        {modernStars.map((star) => {
          let animationStyle;
          switch (star.animation) {
            case 0:
              animationStyle = `${floatDiagonalAnimation} 2s ease-out forwards`;
              break;
            case 1:
              animationStyle = `${pulseAnimation} 2.2s ease-in-out forwards`;
              break;
            case 2:
              animationStyle = `${floatZigzagAnimation} 2.5s ease-in-out forwards`;
              break;
            default:
              animationStyle = `${floatDiagonalAnimation} 2s ease-out forwards`;
          }

          return (
            <ModernStar
              key={star.id}
              sx={{
                left: `${star.x}px`,
                top: `${star.y}px`,
                fontSize: `${star.size}rem`,
                background: `linear-gradient(135deg, ${star.color} 0%, #3b82f6 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                animation: animationStyle,
              }}
            >
              ✦
            </ModernStar>
          );
        })}
      </>,
      document.body,
    );
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        userSelect: "none",
        transition: "all 0.2s ease-in-out",
      }}
      draggable={triggerOnDrag}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
    >
      {children}

      {/* Particles animation rendered through portal */}
      {renderParticles()}

      {/* LGTM Stamp animation rendered through portal */}
      {renderStamp()}
    </Box>
  );
};
